import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
    CheckCircle2, 
    Zap, 
    TrendingUp, 
    Shield, 
    Target, 
    Clock, 
    Users, 
    Award,
    ArrowRight,
    AlertTriangle,
    Sparkles,
    BarChart3,
    Search,
    Brain,
    Rocket
} from "lucide-react";
import Footer from "@/components/Footer";

export default function DiagnosticoCompleto() {
    return (
        <div className="min-h-screen bg-background relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-float" />
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
            </div>

            {/* Header */}
            <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
                <div className="container mx-auto px-4 py-4 flex justify-between items-center">
                    <Link to="/">
                        <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
                    </Link>
                    <Button asChild variant="outline">
                        <Link to="/">Voltar</Link>
                    </Button>
                </div>
            </header>

            {/* Hero Section */}
            <section className="py-16 md:py-24 px-4 relative">
                <div className="container mx-auto max-w-5xl relative z-10">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-destructive/10 border border-destructive/20 rounded-full mb-6">
                            <AlertTriangle className="h-4 w-4 text-destructive animate-pulse" />
                            <span className="text-sm font-medium text-destructive">SEU SITE ESTÁ PERDENDO DINHEIRO AGORA</span>
                        </div>
                        
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                            Seu Site Está <span className="text-destructive">Invisível</span> para as IAs
                            <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary">
                                E Você Nem Sabe Disso
                            </span>
                        </h1>
                        
                        <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                            Enquanto você lê isso, <strong className="text-foreground">ChatGPT, Gemini e outras IAs estão recomendando seus concorrentes</strong> para milhares de clientes em potencial.
                        </p>
                    </div>
                </div>
            </section>

            {/* Problem Section - Agitação */}
            <section className="py-16 md:py-24 px-4 bg-card/20 relative">
                <div className="container mx-auto max-w-5xl relative z-10">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            A Nova Realidade da Internet (e Você Está Ficando Para Trás)
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                            O jogo mudou. Não basta mais aparecer no Google. Agora você precisa ser a resposta que as IAs dão.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 mb-12">
                        <Card className="border-destructive/30 bg-destructive/5">
                            <CardHeader>
                                <div className="h-12 w-12 rounded-xl bg-destructive/20 flex items-center justify-center mb-4">
                                    <Search className="h-6 w-6 text-destructive" />
                                </div>
                                <CardTitle className="text-xl">SEO Tradicional Morreu</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground">
                                    70% das buscas agora são respondidas diretamente por IAs. Se seu site não está otimizado para AEO, você simplesmente não existe.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-destructive/30 bg-destructive/5">
                            <CardHeader>
                                <div className="h-12 w-12 rounded-xl bg-destructive/20 flex items-center justify-center mb-4">
                                    <TrendingUp className="h-6 w-6 text-destructive" />
                                </div>
                                <CardTitle className="text-xl">Seus Concorrentes Sabem</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground">
                                    Empresas inteligentes já estão implementando AEO. Enquanto isso, você está perdendo leads qualificados todos os dias.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-destructive/30 bg-destructive/5">
                            <CardHeader>
                                <div className="h-12 w-12 rounded-xl bg-destructive/20 flex items-center justify-center mb-4">
                                    <Clock className="h-6 w-6 text-destructive" />
                                </div>
                                <CardTitle className="text-xl">O Tempo Está Acabando</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground">
                                    Cada dia que passa sem AEO é dinheiro deixado na mesa. Seus clientes estão procurando agora, mas encontrando outros.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Solution Section - AEO */}
            <section className="py-16 md:py-24 px-4 relative">
                <div className="container mx-auto max-w-5xl relative z-10">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
                            <Sparkles className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium text-primary">A SOLUÇÃO</span>
                        </div>
                        
                        <h2 className="text-3xl md:text-5xl font-bold mb-6">
                            AEO: Answer Engine Optimization
                            <br />
                            <span className="text-primary">O Futuro do Marketing Digital</span>
                        </h2>
                        
                        <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
                            Não otimizamos seu site apenas para aparecer na lista do Google. 
                            <strong className="text-foreground"> Otimizamos para ser a resposta que ChatGPT, Gemini e outras IAs darão aos seus clientes.</strong>
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-12">
                        <Card className="border-primary/30 bg-card/50 backdrop-blur-sm">
                            <CardHeader>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                                        <Brain className="h-6 w-6 text-primary" />
                                    </div>
                                    <CardTitle className="text-xl">O Que é AEO?</CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-muted-foreground">
                                    AEO (Answer Engine Optimization) é a evolução do SEO. Enquanto o SEO tradicional foca em rankings, 
                                    o AEO foca em <strong className="text-foreground">ser a fonte de informação que as IAs usam para responder perguntas</strong>.
                                </p>
                                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                                    <p className="text-sm font-medium text-foreground mb-2">Exemplo Real:</p>
                                    <p className="text-sm text-muted-foreground italic">
                                        "Qual a melhor empresa de desenvolvimento web em Santos?"
                                    </p>
                                    <p className="text-sm text-primary mt-2">
                                        → Com AEO, a IA recomenda SEU site diretamente na resposta.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-primary/30 bg-card/50 backdrop-blur-sm">
                            <CardHeader>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                                        <BarChart3 className="h-6 w-6 text-primary" />
                                    </div>
                                    <CardTitle className="text-xl">Por Que Funciona?</CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-muted-foreground">
                                        <strong className="text-foreground">Dados Estruturados:</strong> Seu site "fala a língua" das IAs
                                    </p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-muted-foreground">
                                        <strong className="text-foreground">Conteúdo Semântico:</strong> Respostas diretas e autoritativas
                                    </p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-muted-foreground">
                                        <strong className="text-foreground">Autoridade Digital:</strong> Você se torna a referência no seu nicho
                                    </p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-muted-foreground">
                                        <strong className="text-foreground">Performance Otimizada:</strong> Sites rápidos são priorizados
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-16 md:py-24 px-4 bg-card/20 relative">
                <div className="container mx-auto max-w-5xl relative z-10">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            O Que Você Ganha Com Nossa Análise Completa
                        </h2>
                        <p className="text-lg text-muted-foreground">
                            Não é apenas um relatório. É um plano de ação para dominar sua presença digital.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <Card className="border-primary/30 hover:border-primary/50 transition-all">
                            <CardHeader>
                                <Target className="h-8 w-8 text-primary mb-3" />
                                <CardTitle className="text-lg">Análise AEO Completa</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-muted-foreground">
                                    Descubra exatamente como as IAs enxergam seu site e o que precisa ser corrigido para ser recomendado.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-primary/30 hover:border-primary/50 transition-all">
                            <CardHeader>
                                <Zap className="h-8 w-8 text-primary mb-3" />
                                <CardTitle className="text-lg">Performance Detalhada</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-muted-foreground">
                                    Análise técnica usando Google PageSpeed Insights. Saiba exatamente o que está deixando seu site lento.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-primary/30 hover:border-primary/50 transition-all">
                            <CardHeader>
                                <Search className="h-8 w-8 text-primary mb-3" />
                                <CardTitle className="text-lg">SEO Avançado</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-muted-foreground">
                                    Auditoria completa de SEO on-page, meta tags, estrutura de URLs e otimizações técnicas.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-primary/30 hover:border-primary/50 transition-all">
                            <CardHeader>
                                <Shield className="h-8 w-8 text-primary mb-3" />
                                <CardTitle className="text-lg">Acessibilidade</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-muted-foreground">
                                    Verificação de conformidade com WCAG e boas práticas de acessibilidade web.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-primary/30 hover:border-primary/50 transition-all">
                            <CardHeader>
                                <Award className="h-8 w-8 text-primary mb-3" />
                                <CardTitle className="text-lg">Melhores Práticas</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-muted-foreground">
                                    Análise de segurança, HTTPS, práticas modernas de desenvolvimento e otimizações.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border-primary/30 hover:border-primary/50 transition-all">
                            <CardHeader>
                                <Rocket className="h-8 w-8 text-primary mb-3" />
                                <CardTitle className="text-lg">Plano de Ação</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-muted-foreground">
                                    Recomendações priorizadas e acionáveis. Você sabe exatamente o que fazer primeiro.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Social Proof */}
            <section className="py-16 md:py-24 px-4 relative">
                <div className="container mx-auto max-w-5xl relative z-10">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            Por Que Confiar na Coddar?
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                <Users className="h-8 w-8 text-primary" />
                            </div>
                            <h3 className="text-2xl font-bold mb-2">+50</h3>
                            <p className="text-muted-foreground">Sites Analisados</p>
                        </div>

                        <div className="text-center">
                            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                <TrendingUp className="h-8 w-8 text-primary" />
                            </div>
                            <h3 className="text-2xl font-bold mb-2">95+</h3>
                            <p className="text-muted-foreground">Score Médio PageSpeed</p>
                        </div>

                        <div className="text-center">
                            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                <Award className="h-8 w-8 text-primary" />
                            </div>
                            <h3 className="text-2xl font-bold mb-2">100%</h3>
                            <p className="text-muted-foreground">Satisfação dos Clientes</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Urgency Section */}
            <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-destructive/10 via-card/20 to-primary/10 relative">
                <div className="container mx-auto max-w-4xl relative z-10">
                    <Card className="border-destructive/30 bg-card/80 backdrop-blur-sm">
                        <CardHeader className="text-center">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-destructive/10 border border-destructive/20 rounded-full mb-4 mx-auto w-fit">
                                <Clock className="h-4 w-4 text-destructive animate-pulse" />
                                <span className="text-sm font-medium text-destructive">OFERTA POR TEMPO LIMITADO</span>
                            </div>
                            <CardTitle className="text-3xl md:text-4xl mb-4">
                                Cada Dia Sem AEO é Dinheiro Perdido
                            </CardTitle>
                            <CardDescription className="text-lg">
                                Enquanto você espera, seus concorrentes estão sendo recomendados por IAs para seus clientes em potencial.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-6">
                                <h4 className="font-semibold text-foreground mb-3 text-center">O Que Você Está Perdendo AGORA:</h4>
                                <div className="space-y-3">
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-muted-foreground">
                                            <strong className="text-foreground">Leads Qualificados:</strong> Pessoas procurando seus serviços agora, mas encontrando concorrentes
                                        </p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-muted-foreground">
                                            <strong className="text-foreground">Autoridade Digital:</strong> Seu nicho está sendo dominado por quem agiu primeiro
                                        </p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-muted-foreground">
                                            <strong className="text-foreground">ROI de Marketing:</strong> Seu investimento em ads está sendo desperdiçado em um site não otimizado
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-16 md:py-24 px-4 relative">
                <div className="container mx-auto max-w-5xl relative z-10">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            Escolha Seu Plano de Análise
                        </h2>
                        <p className="text-lg text-muted-foreground">
                            Investimento que se paga sozinho com o primeiro cliente conquistado
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-12">
                        {/* Plano Básico */}
                        <Card className="border-border/50 hover:border-primary/50 transition-all">
                            <CardHeader>
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <CardTitle className="text-2xl mb-2">Análise Completa</CardTitle>
                                        <CardDescription>Perfeito para começar</CardDescription>
                                    </div>
                                    <div className="px-3 py-1 bg-primary/10 border border-primary/20 rounded-full">
                                        <span className="text-xs font-medium text-primary">POPULAR</span>
                                    </div>
                                </div>
                                <div className="mb-6">
                                    <span className="text-4xl font-bold">R$ 89,90</span>
                                    <span className="text-muted-foreground ml-2">pagamento único</span>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-3 mb-6">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Análise AEO completa</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Performance (PageSpeed Insights)</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">SEO on-page e técnico</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Acessibilidade (WCAG)</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Melhores práticas web</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Relatório em PDF detalhado</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Entrega em até 48 horas</p>
                                    </div>
                                </div>
                                <Button asChild className="w-full" size="lg">
                                    <Link to="/checkout/diagnostico" className="flex items-center gap-2">
                                        Contratar Agora <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Plano Premium */}
                        <Card className="border-primary/50 bg-gradient-to-br from-primary/5 to-accent/5 relative overflow-hidden">
                            <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 text-xs font-bold">
                                MELHOR VALOR
                            </div>
                            <CardHeader>
                                <div className="mb-4">
                                    <CardTitle className="text-2xl mb-2">Análise + Guia de Implementação</CardTitle>
                                    <CardDescription>Análise completa + guia técnico passo a passo</CardDescription>
                                </div>
                                <div className="mb-6">
                                    <span className="text-4xl font-bold">R$ 129,90</span>
                                    <span className="text-muted-foreground ml-2">pagamento único</span>
                                    <p className="text-sm text-primary mt-1">Economize R$ 40 vs. comprar separado</p>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-3 mb-6">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm"><strong>Tudo do plano Análise Completa</strong></p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Guia técnico de implementação</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Passo a passo detalhado de cada melhoria</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Priorização de tarefas por impacto</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Exemplos de código e implementações</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm">Checklist de otimização AEO</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <p className="text-sm font-semibold text-primary">Suporte por email por 7 dias</p>
                                    </div>
                                </div>
                                <Button asChild className="w-full bg-primary hover:bg-primary/90" size="lg">
                                    <Link to="/checkout/manual" className="flex items-center gap-2">
                                        Quero o Melhor Plano <Rocket className="h-4 w-4" />
                                    </Link>
                                </Button>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Garantia e Informações */}
                    <div className="bg-muted/50 border border-border rounded-lg p-6 mb-8">
                        <h3 className="text-lg font-semibold mb-4 text-center">✅ O Que Acontece Depois da Compra?</h3>
                        <div className="grid md:grid-cols-3 gap-6 text-sm">
                            <div className="text-center">
                                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                                    <span className="text-2xl">1️⃣</span>
                                </div>
                                <p className="font-medium mb-1">Pagamento Seguro</p>
                                <p className="text-muted-foreground">Checkout 100% seguro via Stripe/Mercado Pago</p>
                            </div>
                            <div className="text-center">
                                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                                    <span className="text-2xl">2️⃣</span>
                                </div>
                                <p className="font-medium mb-1">Análise Automática</p>
                                <p className="text-muted-foreground">Nosso sistema inicia a análise imediatamente</p>
                            </div>
                            <div className="text-center">
                                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                                    <span className="text-2xl">3️⃣</span>
                                </div>
                                <p className="font-medium mb-1">Relatório no Email</p>
                                <p className="text-muted-foreground">Você recebe tudo em até 48 horas</p>
                            </div>
                        </div>
                    </div>

                    {/* Bonus Section */}
                    <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10">
                        <CardHeader className="text-center">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/30 rounded-full mb-4 mx-auto w-fit">
                                <Sparkles className="h-4 w-4 text-primary" />
                                <span className="text-sm font-medium text-primary">BÔNUS EXCLUSIVO</span>
                            </div>
                            <CardTitle className="text-2xl mb-2">
                                Quer Que Implementemos as Melhorias?
                            </CardTitle>
                            <CardDescription className="text-base">
                                Se decidir contratar nossos serviços de desenvolvimento, <strong className="text-foreground">o valor investido na análise é 100% descontado do projeto final</strong>.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="text-center">
                            <p className="text-sm text-muted-foreground mb-4">
                                Ou seja: você pode testar nossa qualidade praticamente de graça!
                            </p>
                            <Button asChild variant="outline" size="lg">
                                <Link to="/orcamento" className="flex items-center gap-2">
                                    Solicitar Proposta Personalizada <ArrowRight className="h-4 w-4" />
                                </Link>
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 md:py-24 px-4 bg-card/20 relative">
                <div className="container mx-auto max-w-4xl relative z-10">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            Perguntas Frequentes
                        </h2>
                    </div>

                    <div className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Quanto tempo leva para receber o relatório?</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground">
                                    O relatório é gerado automaticamente e enviado para seu email em até 48 horas após a confirmação do pagamento.
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Preciso ter conhecimento técnico para entender?</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground">
                                    Não! Nosso relatório é escrito em linguagem clara e acessível. Explicamos cada problema encontrado e como ele impacta seu negócio, não apenas termos técnicos.
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">O que é AEO e por que é importante?</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground">
                                    AEO (Answer Engine Optimization) é a otimização do seu site para ser recomendado por IAs como ChatGPT, Gemini e assistentes virtuais. 
                                    Com 70% das buscas sendo respondidas diretamente por IAs, se seu site não está otimizado para AEO, você está invisível para a maioria dos usuários.
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Posso implementar as melhorias sozinho?</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground">
                                    Sim! O plano "Análise + Manual" inclui um guia passo a passo para você ou seu desenvolvedor implementar. 
                                    Mas se preferir, podemos fazer tudo por você - e o valor da análise é descontado do projeto.
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Qual a diferença entre os dois planos?</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-muted-foreground">
                                    O plano "Análise Completa" entrega um relatório detalhado com todos os problemas encontrados. 
                                    O plano "Análise + Guia de Implementação" adiciona um guia técnico passo a passo, exemplos de código, priorização de tarefas e suporte por 7 dias.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-16 md:py-24 px-4 relative">
                <div className="container mx-auto max-w-4xl relative z-10">
                    <Card className="bg-gradient-to-br from-primary/20 via-card/50 to-accent/20 border-primary/30 backdrop-blur-xl overflow-hidden relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                        <div className="text-center py-12 md:py-16 px-6 relative z-10">
                            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
                                Não Deixe Seus Concorrentes
                                <br />
                                <span className="text-primary">Dominarem Seu Mercado</span>
                            </h2>

                            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                                Cada dia que passa sem otimização AEO é dinheiro deixado na mesa. 
                                Comece agora e veja seu site ser recomendado pelas IAs.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button asChild size="lg" className="gap-2 group/btn bg-primary text-primary-foreground hover:bg-primary/90">
                                    <Link to="/checkout/manual" className="flex items-center gap-2">
                                        Quero o Plano Completo <Rocket className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                                    </Link>
                                </Button>
                                <Button asChild size="lg" variant="outline" className="gap-2">
                                    <Link to="/checkout/diagnostico" className="flex items-center gap-2">
                                        Começar com Análise Básica
                                    </Link>
                                </Button>
                            </div>

                            <p className="text-sm text-muted-foreground mt-6">
                                🔒 Pagamento 100% seguro • ⚡ Entrega em até 48 horas • 💯 Satisfação garantida
                            </p>
                        </div>
                    </Card>
                </div>
            </section>

            <Footer />
        </div>
    );
}
